import { ChangeEvent, useEffect, useState } from 'react'

import {
	GComboBox,
	GInput,
	GMultiSelect,
	GTooltip
} from 'components/basic-blocks'

import { setObjectSimpleValueAction, toggleScopeAction } from '../../reducer'

import { useStores } from 'hooks'
import {
	FOCUS_OPTIONS,
	SCOPE_OPTIONS,
	TIME_PERIOD_OPTIONS
} from '../../../../constants'
import { TriggerProps } from './types'
import { HelpCircle as AiOutlineQuestionCircle } from 'components/icons'
export const RenderScopeSelectedLabel = (props: {
	options: Array<{ id: string; label: string }>
}) => {
	const { options } = props
	return <div>{options.map((option) => option.label).join(', ')}</div>
}

export const RenderScopeLabel = (Props: {
	option: { id: string; label: string }
}) => {
	const { option } = Props
	return <span>{option.label}</span>
}

export const Trigger = ({
	level,
	rule,
	dispatch,
	errors,
	clearError,
	targetId
}: TriggerProps) => {
	const { assetStore } = useStores()
	const { adAccounts, campaigns } = assetStore
	const [scopeValues, setScopeValues] = useState<
		{ value: string; label: string }[]
	>([])

	useEffect(() => {
		handleScopeChange(rule.trigger.scope)
	}, [])

	const scopeOptions = SCOPE_OPTIONS.filter((o) => o.level === level)
	const handleScopeChange = (scope: string) => {
		if (level === 'website') {
			if (scope === 'WEBSITE') {
				setScopeValues([])
				return
			}

			setScopeValues(
				adAccounts.map((a) => {
					return {
						value: a.id,
						label: a.name
					}
				})
			)
		}

		if (level === 'ad-account') {
			if (scope === 'ACCOUNT') {
				setScopeValues([])
				return
			}

			setScopeValues(
				campaigns
					.filter((c) => c.ad_account_id === targetId)
					.map((c) => {
						return {
							value: c.id,
							label: c.name
						}
					})
			)
		}
	}

	return (
		<section className="flex flex-col gap-4">
			<div className="flex items-center gap-2">
				<span className="text-t-base-blue font-medium text-xl">Trigger</span>
				<GTooltip
					position="top"
					arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent  border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
					contentClassName="border-none font-grotesk mb-2 bg-t-smoke w-[300px] font-medium text-sm text-t-desc-gray"
					content="Configure the main rule trigger, how many clicks in what amount of time, in what scope.">
					<AiOutlineQuestionCircle className=" w-4 h-4 text-t-gray-hold" />
				</GTooltip>
			</div>
			<div className="flex flex-col gap-4">
				<div className="flex items-end">
					<GComboBox<{ value: string; label: string }>
						options={scopeOptions}
						filter={(item, query) => item.label.toString() === query}
						value={scopeOptions.find((o) => o.value === rule.trigger.scope)}
						renderOption={(i) => i.label.toString()}
						renderValue={(i) => i.label.toString()}
						onChange={(i) => {
							dispatch(toggleScopeAction(i.value, level))
							handleScopeChange(i.value)
						}}
						className="w-full"
						disabled={level === 'campaign'}
						label="For traffic on"
						inputClass='text-base'
					/>
					{scopeValues.length > 0 && (
						<GMultiSelect<{ value: string; label: string }>
							name="scope_values"
							options={scopeValues}
							value={scopeValues.filter((scope) =>
								rule.trigger.scope_values.includes(scope.value)
							)}
							placeholder="Select an option"
							onChange={(val) => {
								dispatch(
									setObjectSimpleValueAction(
										'trigger',
										'scope_values',
										val.map((v) => v.value)
									)
								)
								clearError('scope_values')
							}}
							renderSelected={(list) => (
								<RenderScopeSelectedLabel options={list} />
							)}
							renderLabel={(list) => <RenderScopeLabel option={list} />}
							containerClassName="w-full max-w-[50%] pl-3"
							error={errors?.scope_values}
						/>
					)}
				</div>
				<div className="flex gap-6">
					<GInput
						name="max_ad_clicks"
						placeholder="5"
						value={rule.trigger.max_ad_clicks}
						label="Maximum ad clicks"
						className="flex-1"
						type="number"
						min={0}
						onChange={function (e: ChangeEvent<HTMLInputElement>): void {
							dispatch(
								setObjectSimpleValueAction(
									'trigger',
									'max_ad_clicks',
									e.target.value
								)
							)
							clearError('max_ad_clicks')
						}}
						errorPosition="absolute"
						errorClassName="mt-1"
						error={errors?.max_ad_clicks}
						labelClassName='text-t-base-blue font-medium'
						inputClassName='focus:ring-0'
					/>
					<div className="flex flex-1 gap-6 items-end">
						<GInput
							name="period_value"
							placeholder="5"
							value={rule.trigger.period_value}
							type="number"
							min={1}
							onChange={function (e: ChangeEvent<HTMLInputElement>): void {
								dispatch(
									setObjectSimpleValueAction(
										'trigger',
										'period_value',
										e.target.value
									)
								)
								clearError('period_value')
							}}
							label="Within"
							className="w-full"
							inputClassName='focus:ring-0'
							errorPosition="absolute"
							errorClassName="mt-1"
							labelClassName='text-t-base-blue font-medium'
							error={errors?.period_value}
						/>
						<GComboBox
							options={TIME_PERIOD_OPTIONS}
							filter={(item, query) => item.toString() === query}
							value={rule.trigger.period_type}
							renderOption={(i) => i.toString() + 's'}
							renderValue={(i) => i.toString() + 's'}
							inputClass='text-base h-11'
							onChange={function (value: string): void {
								dispatch(
									setObjectSimpleValueAction('trigger', 'period_type', value)
								)
							}}
						/>
					</div>
				</div>
				<div>
					<GComboBox<{ value: string; label: string }>
						options={FOCUS_OPTIONS}
						filter={(item, query) => item.label.toString() === query}
						value={FOCUS_OPTIONS.find((o) => o.value === rule.trigger.focus)}
						renderOption={(i) => i.label.toString()}
						renderValue={(i) => i.label.toString()}
						onChange={(i) =>
							dispatch(setObjectSimpleValueAction('trigger', 'focus', i.value))
						}
						label="From the same"
						inputClass='text-base'
					/>
				</div>
			</div>
		</section>
	)
}
